import earcut from 'earcut';
import { BufferAttribute, BufferGeometry, Vector3 } from 'three';
import { Gnomonic, type Vec3 } from '@map/worldkit';

/**
 * Turns a spherical polygon into a mesh.
 *
 * The ring is triangulated in the gnomonic plane about its own centre — where
 * its great-circle edges are straight lines, so the triangulation is correct
 * for concave coastlines as well as convex territories — and every triangle is
 * then subdivided and pushed back onto the sphere, which is what gives the land
 * its curvature instead of a flat lid.
 */
export interface TerritoryGeometryOptions {
  /** Height above sea level, as a fraction of the planet radius. */
  elevation?: number;
  /**
   * The longest edge any triangle may have in the projection plane, which sets
   * how finely triangles are split.
   *
   * This has to be adaptive rather than a fixed number of splits. Ear clipping
   * happily produces long thin triangles spanning most of a continent, and the
   * flat chord across 30 degrees of arc sags about 3.4% of the radius — far
   * below a 1% elevation, so the ocean shows straight through the middle of the
   * land.
   */
  maxEdge?: number;
}

export function buildTerritoryGeometry(
  ring: readonly Vec3[],
  options: TerritoryGeometryOptions = {},
): BufferGeometry | null {
  if (ring.length < 3) return null;

  const elevation = options.elevation ?? 0.02;
  const maxEdge = options.maxEdge ?? 0.05;

  const centre = normalise(
    ring.reduce((acc, v) => ({ x: acc.x + v.x, y: acc.y + v.y, z: acc.z + v.z }), { x: 0, y: 0, z: 0 }),
  );

  const projection = new Gnomonic(centre);
  const flat = projection.projectRing(ring);
  if (!flat) return null;

  const flatCoords: number[] = [];
  for (const p of flat) flatCoords.push(p.x, p.y);

  const indices = earcut(flatCoords);
  if (indices.length === 0) return null;

  const positions: number[] = [];

  for (let i = 0; i < indices.length; i += 3) {
    const a = flat[indices[i] as number];
    const b = flat[indices[i + 1] as number];
    const c = flat[indices[i + 2] as number];
    if (!a || !b || !c) continue;

    // Split until no edge sags more than the land stands proud of the sea.
    const longest = Math.max(
      Math.hypot(b.x - a.x, b.y - a.y),
      Math.hypot(c.x - b.x, c.y - b.y),
      Math.hypot(a.x - c.x, a.y - c.y),
    );
    const n = Math.min(24, Math.max(1, Math.ceil(longest / maxEdge)));
    for (let row = 0; row < n; row += 1) {
      for (let col = 0; col < n - row; col += 1) {
        const p = (u: number, v: number) => ({
          x: a.x + ((b.x - a.x) * u + (c.x - a.x) * v) / n,
          y: a.y + ((b.y - a.y) * u + (c.y - a.y) * v) / n,
        });

        pushTriangle(positions, projection, elevation, p(col, row), p(col + 1, row), p(col, row + 1));

        if (col + row < n - 1) {
          pushTriangle(
            positions,
            projection,
            elevation,
            p(col + 1, row),
            p(col + 1, row + 1),
            p(col, row + 1),
          );
        }
      }
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
  // Flat shading needs per-face normals, which computeVertexNormals gives us
  // because nothing is indexed or shared between triangles.
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
}

function pushTriangle(
  out: number[],
  projection: Gnomonic,
  elevation: number,
  a: { x: number; y: number },
  b: { x: number; y: number },
  c: { x: number; y: number },
): void {
  const [va, vb, vc] = [projection.unproject(a), projection.unproject(b), projection.unproject(c)];

  // Winding has to be checked per triangle rather than assumed. Whether the
  // projection preserves orientation depends on the frame it builds at the
  // polygon's centre, and a back-facing triangle is culled — which is exactly
  // the black hole that appears in the middle of a continent.
  const ab = { x: vb.x - va.x, y: vb.y - va.y, z: vb.z - va.z };
  const ac = { x: vc.x - va.x, y: vc.y - va.y, z: vc.z - va.z };
  const normal = {
    x: ab.y * ac.z - ab.z * ac.y,
    y: ab.z * ac.x - ab.x * ac.z,
    z: ab.x * ac.y - ab.y * ac.x,
  };
  const outward = normal.x * va.x + normal.y * va.y + normal.z * va.z;
  const ordered = outward >= 0 ? [va, vb, vc] : [va, vc, vb];

  for (const v of ordered) {
    out.push(v.x * (1 + elevation), v.y * (1 + elevation), v.z * (1 + elevation));
  }
}

/** A line loop tracing a territory's edge, lifted just clear of the land. */
export function buildBorderGeometry(ring: readonly Vec3[], elevation = 0.016): BufferGeometry {
  const points: number[] = [];
  for (let i = 0; i < ring.length; i += 1) {
    const a = ring[i] as Vec3;
    const b = ring[(i + 1) % ring.length] as Vec3;
    points.push(a.x * (1 + elevation), a.y * (1 + elevation), a.z * (1 + elevation));
    points.push(b.x * (1 + elevation), b.y * (1 + elevation), b.z * (1 + elevation));
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(points), 3));
  return geometry;
}

export function toVector3(v: Vec3, radius = 1): Vector3 {
  return new Vector3(v.x * radius, v.y * radius, v.z * radius);
}

function normalise(v: Vec3): Vec3 {
  const length = Math.hypot(v.x, v.y, v.z) || 1;
  return { x: v.x / length, y: v.y / length, z: v.z / length };
}
